/**
 * Deploy-window self-healing for stale bundles.
 *
 * Route chunks are content-hashed; after a rollout the old hashes are gone
 * from the server. An open tab that lazy-imports one (React.lazy route
 * navigation, or Vite's preload of a chunk's CSS) gets an error instead of
 * JavaScript — worded "Failed to fetch dynamically imported module" or
 * "Importing a module script failed" depending on the browser — and would
 * otherwise land on the crash screen. One reload picks up the new
 * index.html with matching hashes; the sessionStorage flag stops a reload
 * loop when the *new* build is broken too.
 */

const RELOADED_FLAG = 'chunk-load-reloaded';

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/,
  /Importing a module script failed/,
];

/**
 * True only for chunk-load failures. Plain network errors ("Failed to
 * fetch") must NOT match — aborted API calls raise those too, and a reload
 * cannot fix them.
 */
export function isChunkLoadError(message: string | undefined | null): boolean {
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Set the one-shot flag and reload. Returns false when the flag is already
 * set, or when sessionStorage is unavailable (a reload loop couldn't be
 * prevented there, so the crash screen's manual Reload button is the safer
 * fallback).
 */
function reloadOnce(): boolean {
  try {
    if (sessionStorage.getItem(RELOADED_FLAG)) return false;
    sessionStorage.setItem(RELOADED_FLAG, '1');
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * Reload the page once for a chunk-load failure. Callers skip their error
 * UI/telemetry when this returns true — the page is about to reload anyway.
 */
export function reloadOnceOnChunkLoadError(message: string | undefined | null): boolean {
  return isChunkLoadError(message) && reloadOnce();
}

/**
 * Vite fires `vite:preloadError` when the preload of a lazy route's chunk
 * dependencies fails (e.g. its CSS 404s after a rollout) and rethrows it as
 * a chunk-load error unless prevented. Install once at startup (main.tsx),
 * before any lazy navigation can happen.
 */
export function installChunkReloadListener(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadOnce()) event.preventDefault();
  });
}
